import React, {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import { combinedSVGIcons }  from 's2s-svg-icons-combined';
import styled from "styled-components";
import { colorGrid } from "s2s-themes";
import { getIntlText } from '../js/utilities';

const { CloseIconSVG, MdSearch, AlertIconSVG } = combinedSVGIcons;

const SearchFilterContainer = styled.div`
  display: flex;
  position: relative;
  z-index: 1;
`;

const SearchContainer = styled.div`
  border-radius: 50%;
  color: ${colorGrid.gray9};
  height : 24px;
  left: 8px;
  padding: 2px 2px 2px 8px;
  position: absolute;
  text-align: center;
  top: 6px;
  width: 24px;
  z-index: 3;
`;

const ErrorContainer = styled.div`
  display: flex;
  position: relative;
  width: 100%;
`;

const ErrorArea = styled.div`
  align-items: center;
  background-color: ${colorGrid.red8};
  border-radius: 2px;
  color: ${colorGrid.gray0};
  display : ${props => props.hasError && !props.hasFocus ? 'flex' : 'none'};
  flex: 1;
  padding-bottom: 10px;
  padding-top: 30px;
  position: absolute;
  top: 50%;
  width: 100%;
  z-index: -1;
`;

const ErrorText = styled.div`
  padding-left: 10px;
  padding-right: 10px;
`;

const SearchInput = styled.input`
  background-color: ${colorGrid.gray0};
  border: ${(props) => props.hasError ? `2px solid ${colorGrid.red8}` : '1px solid #cccfd2'};
  border-radius: 20px;
  box-sizing: border-box;
  color: ${colorGrid.gray9};
  cursor: text;
  height: 40px;
  outline: none;
  padding: 0 50px 0 48px;
  width: 100%;

  &:hover {
    border: ${(props) => props.hasError ? `2px solid ${colorGrid.red8}` : '1px solid #243991'};
  };

  &:focus {
    border: 1px solid #243991;
  };

  &:disabled {
    background-color: ${colorGrid.gray1};
    border: 1px solid ${colorGrid.gray4};
    cursor: not-allowed;
  };
`;

const ResetInput = styled.div`
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  display: ${props => props.clearInputIsActive === true ? 'block' : 'none'};
  height: 30px;
  padding: 2px;
  position: absolute;
  right: 5px;
  text-align: center;
  top: 6px;
  width: 40px;
`;

// Please note that SVG's are not converted to styled components - KCN

const DefaultSearchIconStyle = {
  fill: '#7f888f',
  height: '24px',
  width: '24px'
};

// Styles SearchSVG disabled state
const DisabledSearchIconStyle = {
  fill: '#DEE2E6'
};

// Styles SearchSVG error state
const ErrorSearchIconStyle = {
  fill: '#e03131'
};

// Styles SearchSVG focus state
const FocusSearchIconStyle = {
  fill: '#7f888f',
};

// Styles SearchSVG hover state
const HoverSearchIconStyle = {
  fill: '#7f888f'
};

const alertIconStyle = {
  fill: colorGrid.gray0,
  height: '20px',
  paddingLeft: '10px',
  width: '20px'
};

const removeSVGStyle = {
  backgroundColor: 'inherit',
  fill: colorGrid.gray9,
  height: '24px',
  width: '24px'
};

const SVGContainer = styled.span`
  bottom: -1px;
  cursor: pointer;
  position: absolute;
  right: 5px;
`;

const Results = styled.div`
  align-items: center;
  background-color: ${(props) => props.resultCount > 0 ? '#243991' : '#c70816'};
  border-radius: 100px;
  border-style: solid;
  border-width: 1px;
  bottom: 8px;
  color: white;
  display: flex;
  font-family: Open Sans;
  font-size: 14px;
  font-weight: 600;
  height: 23px;
  line-height: 16px;
  outline: none;
  padding-left: 8px;
  padding-right: 24px;
  position: absolute;
  right: 8px;
  top: 7px;
`;

const ResultsContent = styled.div`
  /* padding-top: 4px; */
  margin-right: 10px;
`;

const SearchFilter = (props) => {
  const [clearInputIsActive, setClearInputIsActive] = useState("");
  const [hasError, setHasError] = useState("");
  const [isFocus, setIsFocus] = useState("");
  const [onHover, setOnHover] = useState("");

  const searchFilterInput = useRef(null);

  useEffect(
    () => {
      if (props.searchText){
        //console.log('sssssssss', this.props.searchText);
        searchFilterInput.current.value = props.searchText;
      }
    },
    [],
  );

  if(props.hasError){
    setHasError(props.hasError);
  }

  const clearInput = () => {
    searchFilterInput.current.value = '';
    setClearInputIsActive(false);
  };

  const handleReset = () => {
    clearInput();
    props.cbStringToFilter('');
  };

  const handleFocus = () => {
    if (!props.isDisabled) {
      setIsFocus(true);
      props.cbOnFocus(true);
    }
  };

  const handleBlur = () => {
    if (!props.isDisabled) {
      setIsFocus(false);
      props.cbOnFocus(false);
    }
  };

  const handleChange = (e) => {
    //console.log('handle change on input: ', e.target.value, e, e.nativeEvent);
    props.cbStringToFilter(e.target.value);
    setHasError(false);
  };

  const handleMouseEnter = () => {
    if (!props.isDisabled) {
      setOnHover(true);
      setHasError(false);
    }
  };

  const handleMouseLeave = () => {
    if (!props.isDisabled) {
      setOnHover(false);
    }
  };

  const setSearchSvgStyle = () => {
    let dynamicInputStates;
    if (hasError) {
      dynamicInputStates = {
        ...DefaultSearchIconStyle,
        ...ErrorSearchIconStyle
      };
    } else if (isFocus) {
      dynamicInputStates = {
        ...DefaultSearchIconStyle,
        ...FocusSearchIconStyle
      };
    } else if (onHover) {
      dynamicInputStates = {
        ...DefaultSearchIconStyle,
        ...HoverSearchIconStyle
      };
    } else if (props.isDisabled) {
      dynamicInputStates = {
        ...DefaultSearchIconStyle,
        ...DisabledSearchIconStyle
      };
    } else {
      dynamicInputStates = DefaultSearchIconStyle;
    }
    return dynamicInputStates;
  };

  const renderResultCount = (resultCount, inputElement) => {
    return ((inputElement && typeof inputElement.value !== "undefined" && inputElement.value.length > 0 && resultCount > -1) || (typeof props.searchText !== "undefined" && props.searchText.length > 0)  ?
      <Results resultCount={resultCount}>
        <ResultsContent>
          {resultCount} {resultCount > 1 || resultCount === 0 ? getIntlText(props.intl, 'SEARCH-BAR-RESULTS', ' results') :
            getIntlText(props.intl, 'SEARCH-BAR-RESULT', ' result')}

          <SVGContainer onClick={handleReset}>
            <CloseIconSVG
              svgStyle={{
                fill: 'white',
                height: '18px',
                width: '18px',
                stroke: 'white',
              }}
            />
          </SVGContainer>
        </ResultsContent>
      </Results> : undefined);
  };

  // console.log('SSSSS search filter render', this.props );
  // console.log('SFR STATE:', this.state);
  return (
    <SearchFilterContainer className={props.className} >
      <SearchContainer className='SearchContainer' >
        <MdSearch svgStyle={setSearchSvgStyle()} />
      </SearchContainer>
      <ErrorContainer className="ErrorContainer">
        <SearchInput
          aria-label={getIntlText(props.intl, 'ARIA-INPUT', 'Input')}
          className="SearchInput"
          disabled={props.isDisabled}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          placeholder={!props.isDisabled === true ? props.placeholderText : getIntlText(props.intl, 'PLACEHOLDER-DISABLED', 'DISABLED')}
          ref={searchFilterInput} // StyledComponent property for setting refs - KCN
          type="text"
          hasError={hasError}
        />
        <ErrorArea
          className="ErrorArea"
          hasError={hasError}
        >
          <AlertIconSVG svgStyle={alertIconStyle} />
          <ErrorText className="ErrorText" >
            {props.errorText}
          </ErrorText>
        </ErrorArea>
      </ErrorContainer>
      {renderResultCount(props.searchResultCount, searchFilterInput.current)}
      <ResetInput
        aria-label={getIntlText(props.intl, 'ARIA-LABEL-RESET-INPUT', 'Reset input')}
        className='resetInput'
        onClick={handleReset}
        onKeyDown={handleReset}
        role="button"
        tabIndex={0}
        clearInputIsActive={clearInputIsActive}
      >
        <CloseIconSVG svgStyle={removeSVGStyle} />
      </ResetInput>
    </SearchFilterContainer>
  );
};

SearchFilter.propTypes = {
  cbOnFocus: PropTypes.func,
  cbStringToFilter: PropTypes.func,
  errorText: PropTypes.string,
  hasError: PropTypes.bool,
  isDisabled: PropTypes.bool,
  placeholderText: PropTypes.string
};

SearchFilter.defaultProps = {
  cbStringToFilter: () => { console.log('cbStringToFilter is not defined'); },
  cbOnFocus: () => { console.log('cbOnFocus is not defined'); },
  errorText: undefined,
  hasError: false,
  isDisabled: false,
  placeholderText: ''
};

export default SearchFilter;
